import Cookies from 'js-cookie';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { RootStore } from 'core/stores/root-store';
import { FormControl, FormGroup } from 'shared/libs/form';
import { AsyncState } from 'shared/libs/state';
import { HttpClient } from 'shared/libs/http';
import { UserApiService } from 'user';
import { VerifyAccountDialogStore } from 'feature-auth/verify-account-dialog/verify-account-dialog.store';
import {
   EmployerLoginPayload,
   EmployerLoginResponse,
   EmployerLoginSuccessResponse,
   EmployerLoginVerificationResponse,
} from './employer-login.types';

type EmployerLoginForm = {
   email: FormControl<string>;
   password: FormControl<string>;
};

type EmployerLoginSubmitResult =
   | {
        success: true;
     }
   | {
        success: false;
        status?: number;
        message?: string;
     };

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export class EmployerLoginStore {
   readonly loginState = new AsyncState<EmployerLoginResponse>();
   readonly verifyAccountDialogStore: VerifyAccountDialogStore;
   showPassword = false;
   captchaToken: string | null = null;
   captchaError = '';
   form!: FormGroup<EmployerLoginForm>;

   constructor(
      readonly rootStore: RootStore,
      private readonly t: (key: string) => string,
      private readonly language: string
   ) {
      this.form = new FormGroup({
         email: new FormControl<string>('', [
            (value) => {
               if (!value.trim()) {
                  return this.t('employerLogin.emailRequired');
               }
               if (!emailPattern.test(value)) {
                  return this.t('employerLogin.invalidEmail');
               }
               return '';
            },
         ]),
         password: new FormControl<string>('', [
            (value) => {
               if (!value.trim()) {
                  return this.t('employerLogin.passwordRequired');
               }
               return '';
            },
         ]),
      });
      this.verifyAccountDialogStore = new VerifyAccountDialogStore(rootStore, language);

      makeObservable(this, {
         showPassword: observable,
         captchaToken: observable,
         captchaError: observable,
         submitting: computed,
         setCaptchaToken: action.bound,
         setCaptchaError: action.bound,
         togglePasswordVisibility: action.bound,
      });
   }

   get submitting(): boolean {
      return this.loginState.status === 'pending';
   }

   setCaptchaToken(token: string | null): void {
      this.captchaToken = token;

      if (token) {
         this.captchaError = '';
      }
   }

   setCaptchaError(message: string): void {
      this.captchaError = message;
   }

   togglePasswordVisibility(): void {
      this.showPassword = !this.showPassword;
   }

   private validateForm(): boolean {
      this.form.controls.email.markAsTouched();
      this.form.controls.password.markAsTouched();
      this.form.validate();

      if (!this.captchaToken) {
         this.captchaError = this.t('employerLogin.captchaRequired');
      } else {
         this.captchaError = '';
      }

      return this.form.valid && !!this.captchaToken;
   }

   private trackLoginError(message: string): void {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
         event: 'website_error',
         error_type: 'login error',
         error_message: message,
      });
   }

   private setEmployerCookies(user: EmployerLoginSuccessResponse['response']['user']): void {
      Cookies.set('user_id', String(user.user_id));
      Cookies.set('tokenClient', user.token);
      Cookies.set('employer_role', user.role ?? '');
      Cookies.set('fname', user.company_name ?? '');
      Cookies.set('user_type', user.user_type);
      Cookies.set('employer_id', String(user.employer_id));
   }

   private isSuccessResponse(response: EmployerLoginResponse): response is EmployerLoginSuccessResponse {
      if (response.status !== 200) {
         return false;
      }

      const responsePayload = response.response;

      if (typeof responsePayload !== 'object' || responsePayload === null) {
         return false;
      }

      if (Array.isArray(responsePayload)) {
         return false;
      }

      if (!('user' in responsePayload)) {
         return false;
      }

      const user = (responsePayload as { user?: unknown }).user;

      if (typeof user !== 'object' || user === null || Array.isArray(user)) {
         return false;
      }

      const token = (user as { token?: unknown }).token;
      return typeof token === 'string' && token.length > 0;
   }

   private isVerificationResponse(
      response: EmployerLoginResponse
   ): response is EmployerLoginVerificationResponse {
      return response.status === 200 && 'requires_verification' in response && response.requires_verification;
   }

   async submit(
      {
         onSuccess,
         onVerificationRequired,
         onError,
      }: {
         onSuccess?: (
            response: EmployerLoginSuccessResponse,
            user: EmployerLoginSuccessResponse['response']['user']
         ) => void | Promise<void>;
         onVerificationRequired?: (
            response: EmployerLoginVerificationResponse
         ) => void | Promise<void>;
         onError?: (message: string, status?: number) => void | Promise<void>;
      } = {}
   ): Promise<EmployerLoginSubmitResult> {
      const valid = this.validateForm();
      if (!valid) {
         return { success: false };
      }

      const payload: EmployerLoginPayload = {
         email: this.form.controls.email.value.trim(),
         password: this.form.controls.password.value,
         language: this.language,
      };

      let result: EmployerLoginSubmitResult = { success: false };

      await this.loginState.run(
         async () => HttpClient.post<EmployerLoginResponse>('/users/login', payload),
         {
            onSuccess: async (response) => {
               if (this.isSuccessResponse(response)) {
                  const user = response.response.user;

                  if (user.user_type !== 'recruiter') {
                     const message = this.t('employerLogin.wrongCredentials');
                     this.trackLoginError(message);
                     this.setCaptchaToken(null);
                     result = { success: false, status: 401, message };
                     await onError?.(message, 401);
                     return;
                  }

                  await UserApiService.setContext({
                     user_id: user.user_id,
                     user_type: user.user_type,
                     lang: this.language,
                  });

                  runInAction(() => {
                     this.rootStore.userStore.setUser(user);
                  });

                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                     event: 'login',
                     user_id: user.user_id,
                     authentication_method: 'email',
                  });

                  this.setEmployerCookies(user);
                  result = { success: true };
                  await onSuccess?.(response, user);
                  return;
               }

               if (this.isVerificationResponse(response)) {
                  this.setCaptchaToken(null);
                  result = { success: false, status: response.status, message: response.message };
                  await onVerificationRequired?.(response);
                  return;
               }

               this.trackLoginError(response.message);
               this.setCaptchaToken(null);
               result = { success: false, status: response.status, message: response.message };
               await onError?.(response.message, response.status);
            },
            onError: async (error) => {
               this.trackLoginError(error.message);
               this.setCaptchaToken(null);
               result = { success: false, status: error.status, message: error.message };
               await onError?.(error.message, error.status);
            },
         }
      );

      return result;
   }
}
